import React from 'react';
import Highcharts from 'highcharts';
import { useTheme } from 'styled-components';
import { commonSettings } from '../charts.constants';
import { useChartSettings } from '../hooks/useChartSettings';
import { Chart } from '../Chart';
import { PieChartLoading } from '../PieChart/PieChart.loading';

const INNER_RADIUS = '80%';

interface GetChartSettingsParams {
  backgroundColor: string;
}

const getChartSettings = ({ backgroundColor }: GetChartSettingsParams): Highcharts.Options => ({
  ...commonSettings,

  chart: {
    type: 'solidgauge',
    spacing: [0, 0, 0, 0],
    margin: 0,
  },

  tooltip: {
    enabled: false,
  },

  pane: {
    startAngle: 0,
    endAngle: 360,
    size: '100%',
    background: [
      {
        outerRadius: '100%',
        innerRadius: INNER_RADIUS,
        backgroundColor,
        borderWidth: 0,
      },
    ],
  },

  yAxis: {
    min: 0,
    max: 100,
    lineWidth: 0,
    tickPositions: [],
  },

  plotOptions: {
    solidgauge: {
      dataLabels: {
        enabled: false,
      },
      linecap: 'round',
      stickyTracking: false,
      rounded: true,
      radius: '100%',
    },

    series: {
      animation: {
        duration: 750,
      },
    },
  },
});

export interface GaugeData {
  name: string;
  value: number;
  color?: string;
  backgroundColor?: string;
}

export interface GaugeProps {
  data: GaugeData | null;
  isLoading?: boolean;
  customSettings?: Record<string, any>;
}

export const Gauge: React.FC<GaugeProps> = ({ isLoading, data, customSettings = {} }) => {
  if (isLoading) {
    return <PieChartLoading innerSize={INNER_RADIUS} />;
  }

  if (!data) {
    throw new Error('No data for Gauge chart');
  }

  return <GaugeContent data={data} customSettings={customSettings} />;
};

interface GaugeContentProps {
  data: GaugeData;
  customSettings: Record<string, any>;
}

const GaugeContent: React.FC<GaugeContentProps> = ({ data, customSettings }) => {
  const theme = useTheme();

  const settings = useChartSettings({
    series: [
      {
        name: data.name,
        data: [
          {
            color: data.color || theme.colors.primary['500'],
            y: data.value,
            innerRadius: INNER_RADIUS,
            outerRadius: '100%',
          },
        ],
      },
    ],
    settings: getChartSettings({
      backgroundColor: data.backgroundColor || theme.colors.neutral['100'],
    }),
    customSettings,
  });

  return <Chart options={settings} />;
};
